// src/utils/carteleria/ingresosGestionContenidosCarteleria.js
//
// F3d — INGRESOS de gestión y contenidos de Cartelería para el ER (hook `useGestionCalcs`): histórico + motor.
//   · meses < 2026-10 → lógica histórica congelada (`./historico/useGestionCalcsIngresosHistorico`), idéntico
//     al de antes en todos los años;
//   · meses >= 2026-10 → devengo del motor `calendarioNegocio` (gestión y contenidos; fin de contrato, ventana de
//     contenidos, "el estado manda", D12 no aplica a gestión).
// Criterio del ER (D11, sin cambio): un negocio con facturas del año (`getCuotasFacturadasPorMes` ≠ null) asigna cada
// cuota al MES DE LA FACTURA — camino histórico para todos los meses; solo el negocio SIN factura del año (proyección
// mes a mes) lee el motor desde el corte. D10: el filtro `estado === "Activo"` (un 100 % no entra) no cambia.
//
// Excepción documentada: un negocio con precio TOTAL del contrato (`modo_precio_gestion = 'total_contrato'`, campo
// nuevo de F2 sin equivalente en el código histórico: fee unitario 0 → gestión 0) usa el motor en TODOS los meses; no
// existe un resultado histórico que congelar.
//
// Las conversiones envuelven EXACTAMENTE las de cada país (`convertirAMonedaLocal` del ER, `toPEN` de Perú) y pasan
// por el helper único `convertirMontoCarteleria` (mismos valores y mismo redondeo).
import { getCuotasFacturadasPorMes } from "../../pages/ERv2/estadoResultadosER";
import { calendarioNegocio } from "./calendarioNegocio";
import { convertirMontoCarteleria } from "./convertirMontoCarteleria";
import { combinarPorCorte, esMesHistorico } from "./corteHistorico";
import {
  detalleGestionContenidosHistorico,
  ingresosGestionContenidosMensualAnoAnteriorHistorico,
  ingresosGestionContenidosMensualChileHistorico,
  ingresosGestionContenidosMensualHistorico,
  ingresosGestionContenidosMensualPeruHistorico,
} from "./historico/useGestionCalcsIngresosHistorico";

// Sentinela: fuerza que el helper llame al conversor (el ER redondea también la moneda local / sin moneda).
const SIN_MONEDA = "__SIN_MONEDA__";
const LOCAL = "__LOCAL__";

const elegible = (n) =>
  !(n.eliminada || n.estado !== "Activo" || !n.fecha_inicio) && (parseInt(n.plazo_contrato_meses) || 0) !== 0;
const esPrecioTotal = (n) => n.modo_precio_gestion === "total_contrato" && (parseFloat(n.monto_total_gestion) || 0) > 0;
const ultimoDiaISO = (ano, m) => new Date(ano, m + 1, 0).toISOString().split("T")[0];

// Mapas mes0 → devengo de gestión / contenidos del año (en moneda original).
const devengoPorMes = (cal, ano) => {
  const g = new Map();
  const c = new Map();
  cal.devengo.forEach((d) => {
    if (d.ano !== ano) return;
    if (d.componente === "gestion") g.set(d.mesNum - 1, d.monto);
    else if (d.componente === "contenidos") c.set(d.mesNum - 1, d.monto);
  });
  return { g, c };
};

/**
 * Ingresos de UN negocio desde el motor (Array(12), moneda local). `convertirGestion/Contenidos(monto, m0)` son las
 * conversiones de cada país (Chile: moneda cargada; Perú: contenidos con respaldo a la moneda de gestión).
 */
const ingresosMotorNegocio = (negocio, ano, hoy, convertirGestion, convertirContenidos) => {
  const out = Array(12).fill(0);
  const { g, c } = devengoPorMes(calendarioNegocio(negocio, { hoy }), ano);
  for (let m = 0; m < 12; m++) {
    if (!g.has(m) && !c.has(m)) continue;
    let ingresoLocal = 0;
    if (g.has(m)) ingresoLocal += convertirGestion(g.get(m), m);
    if (c.has(m)) ingresoLocal += convertirContenidos(c.get(m), m);
    out[m] += ingresoLocal;
  }
  return out;
};

// Combinador común: suma por negocio (mismo orden de acumulación que el hook anterior).
const combinarNegocios = ({ negocios, ano, hoy, historicoDe, motorDe }) => {
  const mapa = Array(12).fill(0);
  const hayHistorico = esMesHistorico(ano, 0);
  const hayMotor = !esMesHistorico(ano, 11);
  (negocios || []).forEach((negocio) => {
    if (!elegible(negocio)) return;
    let v;
    if (getCuotasFacturadasPorMes(negocio, ano)) {
      v = historicoDe(negocio); // D11: con factura, mes de la factura (sin cambio)
    } else if (esPrecioTotal(negocio)) {
      v = motorDe(negocio);
    } else {
      const h = hayHistorico ? historicoDe(negocio) : null;
      const mo = hayMotor ? motorDe(negocio) : null;
      v = h && mo ? combinarPorCorte(ano, h, mo) : h || mo;
    }
    v.forEach((x, i) => {
      mapa[i] += x;
    });
  });
  return mapa;
};

/** País de la sesión (`ingresosGestionContenidosMensual`). */
export const ingresosGestionContenidosMensualCombinado = (
  { negocios, selectedYear, currentYear, pais, convertirAMonedaLocal, costosGestionData },
  hoy = new Date()
) =>
  combinarNegocios({
    negocios,
    ano: selectedYear,
    hoy,
    historicoDe: (negocio) =>
      ingresosGestionContenidosMensualHistorico({
        negocios: [negocio],
        selectedYear,
        currentYear,
        pais,
        convertirAMonedaLocal,
        costosGestionData,
      }),
    motorDe: (negocio) => {
      const fechaDe = (m) => {
        const registro = costosGestionData.find(
          (c) =>
            Number(c.negocio_id) === negocio.id &&
            Number(c.mes) === m + 1 &&
            Number(c.ano) === currentYear &&
            c.fecha_factura
        );
        return registro?.fecha_factura?.split("T")[0] || ultimoDiaISO(selectedYear, m);
      };
      const conv = (moneda) => (monto, m) =>
        convertirMontoCarteleria(
          (x) => convertirAMonedaLocal(x, moneda, pais, negocio.id, m + 1, selectedYear, fechaDe(m)),
          monto,
          moneda || SIN_MONEDA,
          LOCAL
        );
      return ingresosMotorNegocio(
        negocio,
        selectedYear,
        hoy,
        conv(negocio.moneda_gestion),
        conv(negocio.moneda_contenidos)
      );
    },
  });

/** Año anterior (`ingresosGestionContenidosMensualAnoAnterior`). */
export const ingresosGestionContenidosMensualAnoAnteriorCombinado = (
  { negocios, añoAnteriorGestion, pais, convertirAMonedaLocal },
  hoy = new Date()
) =>
  combinarNegocios({
    negocios,
    ano: añoAnteriorGestion,
    hoy,
    historicoDe: (negocio) =>
      ingresosGestionContenidosMensualAnoAnteriorHistorico({
        negocios: [negocio],
        añoAnteriorGestion,
        pais,
        convertirAMonedaLocal,
      }),
    motorDe: (negocio) => {
      const conv = (moneda) => (monto, m) =>
        convertirMontoCarteleria(
          (x) =>
            convertirAMonedaLocal(
              x,
              moneda,
              pais,
              negocio.id,
              m + 1,
              añoAnteriorGestion,
              ultimoDiaISO(añoAnteriorGestion, m)
            ),
          monto,
          moneda || SIN_MONEDA,
          LOCAL
        );
      return ingresosMotorNegocio(
        negocio,
        añoAnteriorGestion,
        hoy,
        conv(negocio.moneda_gestion),
        conv(negocio.moneda_contenidos)
      );
    },
  });

/** Chile (`ingresosGestionContenidosMensualChile`, modo combinado). */
export const ingresosGestionContenidosMensualChileCombinado = (
  { negocios, selectedYear, convertirAMonedaLocal },
  hoy = new Date()
) =>
  combinarNegocios({
    negocios,
    ano: selectedYear,
    hoy,
    historicoDe: (negocio) =>
      ingresosGestionContenidosMensualChileHistorico({ negocios: [negocio], selectedYear, convertirAMonedaLocal }),
    motorDe: (negocio) => {
      const conv = (moneda) => (monto, m) =>
        convertirMontoCarteleria(
          (x) =>
            convertirAMonedaLocal(x, moneda, "Chile", negocio.id, m + 1, selectedYear, ultimoDiaISO(selectedYear, m)),
          monto,
          moneda || SIN_MONEDA,
          LOCAL
        );
      return ingresosMotorNegocio(
        negocio,
        selectedYear,
        hoy,
        conv(negocio.moneda_gestion),
        conv(negocio.moneda_contenidos)
      );
    },
  });

/** Perú (`ingresosGestionContenidosMensualPeru`): TC MENSUAL (`dolarMensualPeru[mes]`), como `toPEN` de antes. */
export const ingresosGestionContenidosMensualPeruCombinado = (
  { negocios, selectedYear, dolarMensualPeru, dolarFallback },
  hoy = new Date()
) =>
  combinarNegocios({
    negocios,
    ano: selectedYear,
    hoy,
    historicoDe: (negocio) =>
      ingresosGestionContenidosMensualPeruHistorico({
        negocios: [negocio],
        selectedYear,
        dolarMensualPeru,
        dolarFallback,
      }),
    motorDe: (negocio) => {
      // Misma conversión que `toPEN` del hook anterior (USD/US$ × TC del mes; el resto, directo).
      const conv = (moneda) => (monto, m) =>
        convertirMontoCarteleria(
          (x, desde) => {
            const u = String(desde).toUpperCase();
            return u === "USD" || u === "US$" ? x * (dolarMensualPeru[m] || dolarFallback["Perú"]) : x;
          },
          monto,
          moneda || "PEN",
          "PEN"
        );
      return ingresosMotorNegocio(
        negocio,
        selectedYear,
        hoy,
        conv(negocio.moneda_gestion),
        conv(negocio.moneda_contenidos || negocio.moneda_gestion)
      );
    },
  });

/** Detalle del modal de gestión/contenidos (`detalleGestionContenidosPorMes`): entradas por mes. */
export const detalleGestionContenidosCombinado = (
  { negocios, clientes, edificios, selectedYear, pais, convertirAMonedaLocal },
  hoy = new Date()
) => {
  const detalle = Array(12)
    .fill()
    .map(() => []);
  const hayHistorico = esMesHistorico(selectedYear, 0);
  const hayMotor = !esMesHistorico(selectedYear, 11);
  (negocios || []).forEach((negocio) => {
    if (!elegible(negocio)) return;
    const historico = () =>
      detalleGestionContenidosHistorico({
        negocios: [negocio],
        clientes,
        edificios,
        selectedYear,
        pais,
        convertirAMonedaLocal,
      });
    const facturado = !!getCuotasFacturadasPorMes(negocio, selectedYear);
    const siempreMotor = !facturado && esPrecioTotal(negocio);
    const h = facturado || (!siempreMotor && hayHistorico) ? historico() : null;
    if (facturado) {
      h.forEach((mes, m) => detalle[m].push(...mes));
      return;
    }
    const cal = calendarioNegocio(negocio, { hoy });
    const { g, c } = devengoPorMes(cal, selectedYear);
    const [iy, im] = String(negocio.fecha_inicio).split("T")[0].split("-").map(Number);
    const pantallas = parseInt(negocio.cantidad_pantallas) || 1;
    const gestionUnitario = parseFloat(negocio.fee_gestion_unitario) || 0;
    const contenidosUnitario = negocio.incluye_contenidos ? parseFloat(negocio.fee_contenidos_unitario) || 0 : 0;
    let nombreReferencia = `ID ${negocio.referencia_id}`;
    if (negocio.tipo_vertical === "TD" && clientes[negocio.referencia_id]) {
      nombreReferencia = clientes[negocio.referencia_id].nombre;
    } else if (negocio.tipo_vertical === "EO" && edificios[negocio.referencia_id]) {
      nombreReferencia = edificios[negocio.referencia_id]?.edificio;
    }
    for (let m = 0; m < 12; m++) {
      if (!siempreMotor && esMesHistorico(selectedYear, m)) {
        if (h) detalle[m].push(...h[m]);
        continue;
      }
      if (!hayMotor && !siempreMotor) continue;
      // Ventana del servicio según el motor (una entrada por mes, aunque el monto sea 0, como antes).
      const idx = (selectedYear - iy) * 12 + (m + 1 - im);
      if (idx < 0 || idx >= cal.mesesVentana) continue;
      const aLocal = (monto, moneda) =>
        convertirMontoCarteleria(
          (x) => convertirAMonedaLocal(x, moneda, pais, negocio.id, m + 1, selectedYear),
          monto,
          moneda || SIN_MONEDA,
          LOCAL
        );
      const gestionLocal = g.has(m) ? aLocal(g.get(m), negocio.moneda_gestion) : 0;
      const contenidosLocal = c.has(m) ? aLocal(c.get(m), negocio.moneda_contenidos) : 0;
      detalle[m].push({
        id: negocio.id,
        tipo: negocio.tipo_vertical,
        nombre: nombreReferencia,
        pantallas,
        gestionUnitario,
        gestionMoneda: negocio.moneda_gestion,
        contenidosUnitario,
        contenidosMoneda: negocio.moneda_contenidos,
        totalLocal: gestionLocal + contenidosLocal,
      });
    }
  });
  return detalle.map((mes) =>
    mes.sort((a, b) => {
      if (a.tipo !== b.tipo) return a.tipo === "EO" ? -1 : 1;
      return a.nombre.localeCompare(b.nombre);
    })
  );
};
